import { useState, useMemo, type FC } from "react";
import { ChevronDownIcon, ChevronRightIcon, CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import { CalculatorLogRenderer } from "./tool-renderers/calculator-log-renderer";
import { FileLink, extractFileOutputs } from "./file-viewer";
import { ResourceFormWrapper, isResourceFormMessage, ResourceFormMessage } from "./resource-form-message";
import { isResourceListMessage, ResourceListMessage } from "./resource-list-message";
import { MarkdownText } from "./markdown-text";
import { JsonCodeBlock } from "./json-code-block";
import { JsonDisplay } from "./json-display";

// Registry of custom tool result renderers
type ToolResultRendererProps = {
  toolName: string;
  toolInput: Record<string, unknown>;
  result: unknown;
  isError: boolean;
  durationMs: number;
};

const toolResultRenderers: Record<string, FC<ToolResultRendererProps>> = {
  "calculator": CalculatorLogRenderer,
  "calculate": CalculatorLogRenderer,
  "math": CalculatorLogRenderer,
  "test_calculator": CalculatorLogRenderer,
};

/**
 * Event types from backend
 */
export type ExecutionEvent =
  | LLMEndResponseEvent
  | ToolCallStartedEvent
  | ToolProgressEvent
  | ToolCallCompletedEvent
  | ExecutionCompletedEvent
  | SubagentStartedEvent
  | SubagentCompletedEvent
  | ToolsFinderStartedEvent
  | ToolsFinderCompletedEvent
  | AsyncToolCheckpointEvent
  | LLMStartEvent
  | LLMEndEvent
  | AgentStartEvent;

export interface LLMEndResponseEvent {
  type: "llm_end";
  execution_id?: string;  // Which agent produced this event
  messageId: string;  // Associate with assistant message
  content: Array<{ type: string; text?: string; id?: string; name?: string; input?: unknown }>;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  duration_ms: number;
  model: string;
  stop_reason: string;
  timestamp: number;
}

export interface ToolCallStartedEvent {
  type: "tool_call_started";
  execution_id?: string;  // Which agent produced this event
  messageId: string;  // Associate with assistant message
  tool_use_id: string;
  tool_name: string;
  tool_input: Record<string, unknown>;
  is_async: boolean;
  timestamp: number;
}

export interface ToolProgressEvent {
  type: "tool_progress";
  execution_id?: string;  // Which agent produced this event
  messageId: string;  // Associate with assistant message
  tool_call_id: string;  // Maps to tool_use_id
  tool_name: string;
  progress: {
    percent_complete: number;
    state: string;
  };
  timestamp: number;
}

export interface ToolCallCompletedEvent {
  type: "tool_call_completed";
  execution_id?: string;  // Which agent produced this event
  messageId: string;  // Associate with assistant message
  tool_use_id: string;
  tool_name: string;
  result: unknown;
  is_error: boolean;
  duration_ms: number;
  timestamp: number;
}

export interface ExecutionCompletedEvent {
  type: "execution_completed";
  execution_id?: string;  // Which agent produced this event
  messageId: string;  // Associate with assistant message
  duration_ms: number;
  total_input_tokens: number;
  total_output_tokens: number;
  cost_usd: number;
  timestamp: number;
}

export interface SubagentStartedEvent {
  type: "subagent_started";
  execution_id: string;  // This sub-agent's ID
  parent_execution_id: string;  // Parent agent's ID
  messageId: string;  // Associate with assistant message
  agent_type: string;
  goal: string;
  timestamp: number;
}

export interface SubagentCompletedEvent {
  type: "subagent_completed";
  execution_id: string;  // This sub-agent's ID
  parent_execution_id: string;  // Parent agent's ID
  messageId: string;  // Associate with assistant message
  duration_ms: number;
  total_input_tokens: number;
  total_output_tokens: number;
  cost_usd: number;
  timestamp: number;
}

export interface ToolsFinderStartedEvent {
  type: "tools_finder_started";
  execution_id?: string;
  messageId: string;
  unavailable_tools: string[];
  count: number;
  timestamp: number;
}

export interface ToolsFinderCompletedEvent {
  type: "tools_finder_completed";
  execution_id?: string;
  messageId: string;
  total_tools: number;
  alternatives_found: number;
  results: Record<string, unknown>;
  timestamp: number;
}

export interface AsyncToolCheckpointEvent {
  type: "async_tool_checkpoint";
  execution_id?: string;
  messageId: string;
  tool_use_id: string;
  tool_name: string;
  checkpoint_data?: Record<string, unknown>;
  timestamp: number;
}

// ProCode subagent LLM lifecycle events (from OpenAI Agents SDK hooks)
export interface LLMStartEvent {
  type: "llm_start";
  execution_id?: string;  // Which subagent produced this event
  messageId: string;
  agent_name?: string;
  timestamp: number;
}

export interface LLMEndEvent {
  type: "llm_end";
  execution_id?: string;  // Which subagent produced this event
  messageId: string;
  agent_name?: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  timestamp: number;
}

export interface AgentStartEvent {
  type: "agent_start";
  execution_id?: string;  // Which subagent produced this event
  messageId: string;
  agent_name?: string;
  timestamp: number;
}

interface ExecutionLogProps {
  events: ExecutionEvent[];
  isRunning?: boolean;
  isWaiting?: boolean;
  onFormSubmit?: (message: string) => void;  // For form submissions in tool results
  powerMode?: boolean;  // Hide tool calls, costs, tokens - show only text and agent structure
}

/**
 * Execution tree node - groups events by execution_id
 */
interface ExecutionNode {
  execution_id: string;
  parent_execution_id: string | null;
  agent_type?: string;
  goal?: string;
  startTimestamp?: number;  // When the subagent started (from subagent_started event)
  events: ExecutionEvent[];
  children: ExecutionNode[];
  isComplete: boolean;
  completionStats?: {
    duration_ms: number;
    total_input_tokens: number;
    total_output_tokens: number;
    cost_usd: number;
  };
}

/**
 * Build execution tree from flat event stream
 * Groups events by execution_id and establishes parent-child relationships
 */
function buildExecutionTree(events: ExecutionEvent[]): ExecutionNode {
  const nodes = new Map<string, ExecutionNode>();

  // Find root execution_id by looking at multiple sources:
  // 1. First try to find from subagent_started events - the parent_execution_id
  //    of top-level subagents IS the root execution_id
  // 2. Fall back to first llm_response/agent_completed execution_id
  // 3. Fall back to "root" as a placeholder

  // Collect all subagent execution_ids (only valid ones)
  const subagentIds = new Set<string>();
  events.forEach(e => {
    if (e.type === "subagent_started" && e.execution_id) {
      subagentIds.add(e.execution_id);
    }
  });

  // Find root: a parent_execution_id that is NOT itself a subagent
  let rootId: string | undefined;
  for (const e of events) {
    if (e.type === "subagent_started" && e.parent_execution_id && !subagentIds.has(e.parent_execution_id)) {
      rootId = e.parent_execution_id;
      break;
    }
  }

  // Fall back to llm_end/execution_completed execution_id
  if (!rootId) {
    rootId = events.find(e =>
      (e.type === "llm_end" || e.type === "execution_completed") && e.execution_id
    )?.execution_id || "root";
  }

  // Create root node
  const root: ExecutionNode = {
    execution_id: rootId,
    parent_execution_id: null,
    events: [],
    children: [],
    isComplete: false,
  };
  nodes.set(rootId, root);

  // Process events in order
  for (const event of events) {
    if (event.type === "subagent_started") {
      // Skip invalid events (missing required IDs)
      if (!event.execution_id) {
        console.warn('[execution-log] Skipping subagent_started with missing execution_id:', event);
        continue;
      }

      // Check if node already exists (created by earlier events)
      let child = nodes.get(event.execution_id);

      if (child) {
        // Node exists - update its properties from subagent_started
        child.parent_execution_id = event.parent_execution_id || null;
        child.agent_type = event.agent_type;
        child.goal = event.goal;
        child.startTimestamp = event.timestamp;
        // Reset completion state - subagent_started means it's running
        child.isComplete = false;
        child.completionStats = undefined;
      } else {
        // Create new child node
        child = {
          execution_id: event.execution_id,
          parent_execution_id: event.parent_execution_id || null,
          agent_type: event.agent_type,
          goal: event.goal,
          startTimestamp: event.timestamp,  // Store when subagent started for ordering
          events: [],
          children: [],
          isComplete: false,
        };
        nodes.set(event.execution_id, child);
      }

      // Add the subagent_started event to the node's events for rendering
      child.events.push(event);

      // Find parent and add child to it (only if not already added)
      // Use parent_execution_id if available, otherwise attach to root
      const parentId = event.parent_execution_id;
      const parent = parentId ? nodes.get(parentId) : null;
      if (parent) {
        if (!parent.children.includes(child)) {
          parent.children.push(child);
        }
      } else {
        // Parent not found or no parent_execution_id, add to root
        if (!root.children.includes(child)) {
          root.children.push(child);
        }
      }
    } else if (event.type === "subagent_completed") {
      // Skip invalid events (missing required IDs)
      if (!event.execution_id) {
        console.warn('[execution-log] Skipping subagent_completed with missing execution_id:', event);
        continue;
      }

      // Mark node as complete and store stats
      const node = nodes.get(event.execution_id);
      if (node) {
        node.isComplete = true;
        node.completionStats = {
          duration_ms: event.duration_ms || 0,
          total_input_tokens: event.total_input_tokens || 0,
          total_output_tokens: event.total_output_tokens || 0,
          cost_usd: event.cost_usd || 0,
        };
        // Add the completion event to the node's events for rendering
        node.events.push(event);
      } else {
        console.warn('[execution-log] subagent_completed for unknown node:', event.execution_id);
      }
    } else if (event.type === "execution_completed") {
      // Mark root as complete
      const execId = event.execution_id || rootId;
      const node = nodes.get(execId);
      if (node) {
        node.isComplete = true;
        node.completionStats = {
          duration_ms: event.duration_ms,
          total_input_tokens: event.total_input_tokens,
          total_output_tokens: event.total_output_tokens,
          cost_usd: event.cost_usd,
        };
      }
      // Also add to events for rendering
      const targetNode = nodes.get(execId) || root;
      targetNode.events.push(event);
    } else {
      // Regular event - add to appropriate node
      const execId: string = event.execution_id || rootId;
      let node = nodes.get(execId);

      // If node doesn't exist, check if it's a known subagent execution_id
      // This can happen if events arrive before subagent_started
      if (!node) {
        const isKnownSubagent = subagentIds.has(execId);
        if (isKnownSubagent) {
          // Create the node now (it will be properly linked when subagent_started is processed)
          const newNode: ExecutionNode = {
            execution_id: execId,
            parent_execution_id: null,  // Will be set by subagent_started
            events: [],
            children: [],
            isComplete: false,
          };
          nodes.set(execId, newNode);
          node = newNode;
        } else {
          // Unknown execution_id - add to root
          node = root;
        }
      }

      node.events.push(event);
    }
  }

  return root;
}

/**
 * Check if we have any nested execution (subagent events)
 */
function hasNestedExecutions(events: ExecutionEvent[]): boolean {
  return events.some(e => e.type === "subagent_started" || e.type === "subagent_completed");
}

/**
 * Execution Log - Displays agent events as a flowing timeline
 * Supports nested sub-agent rendering when execution_id is present
 */
export function ExecutionLog({ events, isRunning, isWaiting, onFormSubmit, powerMode = false }: ExecutionLogProps) {
  if (events.length === 0 && !isRunning && !isWaiting) {
    return null;
  }

  // Build lookup map for tool inputs (from tool_call_started events)
  const toolInputs = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    events.forEach(event => {
      if (event.type === "tool_call_started") {
        map.set(event.tool_use_id, event.tool_input);
      }
    });
    return map;
  }, [events]);

  // Build lookup map for tool progress (latest progress for each tool_call_id)
  const toolProgress = useMemo(() => {
    const map = new Map<string, { percent_complete: number; state: string }>();
    events.forEach(event => {
      if (event.type === "tool_progress") {
        // Keep overwriting with latest progress
        map.set(event.tool_call_id, event.progress);
      }
    });
    return map;
  }, [events]);

  // Build set of completed LLM calls (execution_id + agent_name pairs that have llm_end)
  // Used to hide llm_start "thinking..." when llm_end has arrived
  const completedLLMCalls = useMemo(() => {
    const set = new Set<string>();
    events.forEach(event => {
      if (event.type === "llm_end") {
        // Key by execution_id + agent_name to match start/end pairs
        const key = `${event.execution_id || ''}:${event.agent_name || ''}`;
        set.add(key);
      }
    });
    return set;
  }, [events]);

  // Build set of agents that have progressed past start (have llm_start or later events)
  // Used to hide agent_start "starting..." when agent has progressed
  const progressedAgents = useMemo(() => {
    const set = new Set<string>();
    events.forEach(event => {
      if (event.type === "llm_start" || event.type === "llm_end") {
        // Key by execution_id + agent_name
        const execId = event.execution_id || '';
        const agentName = (event as { agent_name?: string }).agent_name || '';
        const key = `${execId}:${agentName}`;
        set.add(key);
      }
    });
    return set;
  }, [events]);

  // Check if we need tree rendering (has nested executions)
  const useTreeRendering = useMemo(() => hasNestedExecutions(events), [events]);

  // Build tree if needed
  const rootNode = useMemo(() =>
    useTreeRendering ? buildExecutionTree(events) : null
  , [events, useTreeRendering]);

  return (
    <div className="font-mono text-sm space-y-1 text-foreground">
      {useTreeRendering && rootNode ? (
        // Tree rendering for nested executions
        <ExecutionNodeRenderer node={rootNode} toolInputs={toolInputs} toolProgress={toolProgress} completedLLMCalls={completedLLMCalls} progressedAgents={progressedAgents} depth={0} onFormSubmit={onFormSubmit} powerMode={powerMode} />
      ) : (
        // Flat rendering for simple executions (backward compatible)
        events.map((event, idx) => (
          <EventEntry key={idx} event={event} toolInputs={toolInputs} toolProgress={toolProgress} completedLLMCalls={completedLLMCalls} progressedAgents={progressedAgents} onFormSubmit={onFormSubmit} powerMode={powerMode} />
        ))
      )}
      {(isRunning || isWaiting) && (
        <div className="flex items-center gap-2 text-blue-400 animate-pulse">
          <span className="w-2 h-2 bg-blue-400 rounded-full" />
          <span>{isWaiting ? 'Waiting...' : 'Processing...'}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Recursive renderer for execution tree nodes
 */
function ExecutionNodeRenderer({
  node,
  toolInputs,
  toolProgress,
  completedLLMCalls,
  progressedAgents,
  depth,
  onFormSubmit,
  powerMode = false
}: {
  node: ExecutionNode;
  toolInputs: Map<string, Record<string, unknown>>;
  toolProgress: Map<string, { percent_complete: number; state: string }>;
  completedLLMCalls: Set<string>;
  progressedAgents: Set<string>;
  depth: number;
  onFormSubmit?: (message: string) => void;
  powerMode?: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const isSubagent = depth > 0;

  return (
    <div className={cn(isSubagent && "ml-4 border-l-2 border-purple-500/50")}>
      {/* Sub-agent header (only for non-root nodes) */}
      {isSubagent && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-purple-400 hover:text-purple-300 py-1 pl-3 w-full text-left transition-colors"
        >
          {expanded ? (
            <ChevronDownIcon className="w-3 h-3" />
          ) : (
            <ChevronRightIcon className="w-3 h-3" />
          )}
          <span className="font-semibold">SUBAGENT</span>
          <span className="text-muted-foreground">{node.agent_type || "unknown"}</span>
          <span className="text-purple-400">[async]</span>
          {node.isComplete && (
            <>
              <CheckCircledIcon className="w-3 h-3 text-green-400" />
              {!powerMode && node.completionStats && (
                <>
                  <span className="text-muted-foreground">|</span>
                  <span className="text-muted-foreground">{(node.completionStats.duration_ms / 1000).toFixed(1)}s</span>
                  <span className="text-muted-foreground">|</span>
                  <span className="text-amber-400">
                    {node.completionStats.total_input_tokens}+{node.completionStats.total_output_tokens} tok
                  </span>
                </>
              )}
            </>
          )}
          {!node.isComplete && (
            <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
          )}
        </button>
      )}

      {/* Goal display for sub-agents */}
      {isSubagent && expanded && node.goal && (
        <div className="pl-8 text-muted-foreground text-[10px] mb-1 truncate">
          Goal: {node.goal}
        </div>
      )}

      {/* Events and children */}
      {(expanded || !isSubagent) && (
        <div className={cn(isSubagent && "pl-3")}>
          {/* Render events for this node, interleaved with children */}
          {renderEventsWithChildren(node, toolInputs, toolProgress, completedLLMCalls, progressedAgents, depth, onFormSubmit, powerMode)}
        </div>
      )}
    </div>
  );
}

/**
 * Render events interleaved with child nodes based on timestamp order
 */
function renderEventsWithChildren(
  node: ExecutionNode,
  toolInputs: Map<string, Record<string, unknown>>,
  toolProgress: Map<string, { percent_complete: number; state: string }>,
  completedLLMCalls: Set<string>,
  progressedAgents: Set<string>,
  depth: number,
  onFormSubmit?: (message: string) => void,
  powerMode: boolean = false
): React.ReactNode[] {
  // Create a combined list of events and child node markers with timestamps
  type RenderItem =
    | { type: 'event'; event: ExecutionEvent; timestamp: number }
    | { type: 'child'; child: ExecutionNode; timestamp: number };

  const items: RenderItem[] = [];

  // Add events
  node.events.forEach(event => {
    items.push({ type: 'event', event, timestamp: event.timestamp || 0 });
  });

  // Add children using their startTimestamp (from subagent_started event)
  // This ensures children appear at the correct position in the timeline
  node.children.forEach(child => {
    // Use the stored startTimestamp, fallback to first event or 0
    const timestamp = child.startTimestamp || child.events[0]?.timestamp || 0;
    items.push({ type: 'child', child, timestamp });
  });

  // Sort by timestamp (stable sort preserves order for same timestamp)
  items.sort((a, b) => a.timestamp - b.timestamp);

  // Render in order
  return items.map((item, idx) => {
    if (item.type === 'event') {
      return <EventEntry key={`event-${idx}`} event={item.event} toolInputs={toolInputs} toolProgress={toolProgress} completedLLMCalls={completedLLMCalls} progressedAgents={progressedAgents} onFormSubmit={onFormSubmit} powerMode={powerMode} />;
    } else {
      return (
        <ExecutionNodeRenderer
          key={`child-${item.child.execution_id}`}
          node={item.child}
          toolInputs={toolInputs}
          toolProgress={toolProgress}
          completedLLMCalls={completedLLMCalls}
          progressedAgents={progressedAgents}
          depth={depth + 1}
          onFormSubmit={onFormSubmit}
          powerMode={powerMode}
        />
      );
    }
  });
}

function EventEntry({ event, toolInputs, toolProgress, completedLLMCalls, progressedAgents, onFormSubmit, powerMode = false }: {
  event: ExecutionEvent;
  toolInputs: Map<string, Record<string, unknown>>;
  toolProgress: Map<string, { percent_complete: number; state: string }>;
  completedLLMCalls: Set<string>;
  progressedAgents: Set<string>;
  onFormSubmit?: (message: string) => void;
  powerMode?: boolean;
}) {
  switch (event.type) {
    case "llm_end":
      return <LLMEndResponseEntry event={event} powerMode={powerMode} />;
    case "tool_call_started":
      // Hide tool calls in power mode
      if (powerMode) return null;
      return <ToolCallEntry event={event} progress={toolProgress.get(event.tool_use_id)} />;
    case "tool_progress":
      // Progress is displayed on the tool_call_started entry, not separately
      return null;
    case "tool_call_completed":
      // Hide tool results in power mode
      if (powerMode) return null;
      return <ToolCallCompletedEntry event={event} toolInput={toolInputs.get(event.tool_use_id) || {}} onFormSubmit={onFormSubmit} />;
    case "execution_completed":
      // Hide completion stats in power mode
      if (powerMode) return null;
      return <ExecutionCompletedEntryRenderer event={event} />;
    case "subagent_started":
    case "subagent_completed":
      // These are handled by the tree structure, not rendered directly
      return null;
    case "tools_finder_started":
      // Hide tools finder in power mode
      if (powerMode) return null;
      return <ToolsFinderStartedEntry event={event} />;
    case "tools_finder_completed":
      // Hide tools finder in power mode
      if (powerMode) return null;
      return <ToolsFinderCompletedEntry event={event} />;
    case "async_tool_checkpoint":
      // Hide checkpoints in power mode
      if (powerMode) return null;
      return <AsyncToolCheckpointEntry event={event} />;
    case "llm_start": {
      // Hide LLM lifecycle events in power mode
      if (powerMode) return null;
      // Hide "thinking..." if LLM has completed (llm_end received)
      const llmKey = `${event.execution_id || ''}:${event.agent_name || ''}`;
      if (completedLLMCalls.has(llmKey)) return null;
      return <LLMStartEntry event={event} />;
    }
    // Note: llm_end is handled at line 631 with LLMEndResponseEntry
    // The LLMEndEvent type (lifecycle event from subagents) shares the same type string
    // but is filtered out by the main case since it lacks content/duration_ms fields
    case "agent_start": {
      // Hide agent lifecycle events in power mode
      if (powerMode) return null;
      // Hide "starting..." if agent has progressed (has llm_start or later)
      const agentKey = `${event.execution_id || ''}:${event.agent_name || ''}`;
      if (progressedAgents.has(agentKey)) return null;
      return <AgentStartEntry event={event} />;
    }
    default:
      return null;
  }
}

/**
 * Check if text contains markdown code blocks (including mermaid)
 */
function containsMarkdown(text: string): boolean {
  // Check for code blocks (```) or mermaid diagrams
  return /```[\s\S]*?```/.test(text) ||
         /^#{1,6}\s/.test(text) || // Headers
         /\*\*[^*]+\*\*/.test(text) || // Bold
         /\|[^|]+\|/.test(text); // Tables
}

/**
 * Check if text looks like structured data (JSON or Python dict/list)
 */
function isStructuredData(text: string): boolean {
  const trimmed = text.trim();
  // Must start with { or [
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return false;
  // Must end with } or ]
  if (!trimmed.endsWith('}') && !trimmed.endsWith(']')) return false;
  // Check for valid JSON
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    // Also accept Python dict-like strings (single quotes, True/False/None)
    // e.g. {'status': 'error', 'value': True}
    if (/^[\{\[]/.test(trimmed) && /[\}\]]$/.test(trimmed)) {
      // Has key-value pattern with quotes
      return /'[^']+'\s*:/.test(trimmed) || /"[^"]+"\s*:/.test(trimmed);
    }
    return false;
  }
}

/**
 * Expandable text content - shows truncated with "Show more" link
 * Supports markdown rendering including mermaid diagrams
 * JSON content is wrapped in code blocks for syntax highlighting
 */
function ExpandableText({
  text,
  previewLength = 300,
  className = ""
}: {
  text: string;
  previewLength?: number;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = text.length > previewLength;
  const hasMarkdown = containsMarkdown(text);
  const isStructured = isStructuredData(text);

  // For structured data (JSON/dict), parse and display nicely
  if (isStructured) {
    try {
      const parsed = JSON.parse(text);
      return (
        <div className={cn("text-foreground max-w-full overflow-hidden", className)}>
          <JsonDisplay data={parsed} />
        </div>
      );
    } catch {
      // If JSON parse fails (e.g., Python dict), fall through to text display
    }
  }

  // For markdown content, always show full content with markdown rendering
  if (hasMarkdown) {
    return (
      <div className={cn("text-foreground max-w-full overflow-hidden", className)}>
        <MarkdownText text={text} />
      </div>
    );
  }

  if (!needsTruncation) {
    return (
      <div className={cn("text-foreground whitespace-pre-wrap break-words max-w-full overflow-hidden", className)}>
        {text}
      </div>
    );
  }

  return (
    <div className={cn("text-foreground max-w-full overflow-hidden", className)}>
      <div className="whitespace-pre-wrap break-words">
        {expanded ? text : text.slice(0, previewLength) + "..."}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1"
      >
        {expanded ? (
          <>
            <ChevronDownIcon className="w-3 h-3 rotate-180" />
            Show less
          </>
        ) : (
          <>
            <ChevronRightIcon className="w-3 h-3" />
            Show more ({Math.round(text.length / 1000)}k chars)
          </>
        )}
      </button>
    </div>
  );
}

function LLMEndResponseEntry({ event, powerMode = false }: { event: LLMEndResponseEvent; powerMode?: boolean }) {
  // Extract text content
  const textContent = event.content
    .filter(b => b.type === "text" && b.text)
    .map(b => b.text)
    .join("");

  // Check if there are tool calls
  const hasToolCalls = event.content.some(b => b.type === "tool_use");

  // Check if the text is a structured resource message
  const isStructuredMessage = textContent && (
    isResourceFormMessage(textContent) || isResourceListMessage(textContent)
  );

  // In power mode, only show if there's text content
  if (powerMode && !textContent) {
    return null;
  }

  // Determine label based on message type
  const label = isStructuredMessage
    ? (isResourceListMessage(textContent) ? "LIST" : "FORM")
    : "LLM";

  return (
    <div className={cn("pl-3 py-1", !powerMode && "border-l-2 border-emerald-500")}>
      {/* Hide stats header in power mode */}
      {!powerMode && (
        <div className="flex items-center gap-2 text-emerald-400">
          <span className="font-semibold">{label}</span>
          <span className="text-muted-foreground">|</span>
          <span className="text-muted-foreground">{event.duration_ms}ms</span>
          <span className="text-muted-foreground">|</span>
          <span className="text-amber-400">{event.input_tokens}+{event.output_tokens} tok</span>
          <span className="text-muted-foreground">|</span>
          <span className="text-green-400">${event.cost_usd.toFixed(4)}</span>
        </div>
      )}
      {textContent && (
        <div className={cn(!powerMode && "mt-1")}>
          {isStructuredMessage ? (
            <ResourceFormWrapper text={textContent} />
          ) : (
            <ExpandableText text={textContent} previewLength={powerMode ? 1000 : 300} />
          )}
        </div>
      )}
      {/* Hide tool call indicator in power mode */}
      {!powerMode && hasToolCalls && (
        <div className="text-blue-400 mt-1">
          {event.stop_reason === "tool_use" && "→ Tool call requested"}
        </div>
      )}
    </div>
  );
}

function ToolCallEntry({ event, progress }: {
  event: ToolCallStartedEvent;
  progress?: { percent_complete: number; state: string };
}) {
  const [expanded, setExpanded] = useState(false);

  // Format tool name for display (remove mcp__ prefix, replace __ with spaces)
  const formatToolName = (name: string) => {
    return name.replace(/^mcp__/, '').replace(/__/g, ' › ');
  };

  return (
    <div className="border-l-2 border-blue-500 pl-3 py-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors w-full text-left"
      >
        {expanded ? (
          <ChevronDownIcon className="w-3 h-3" />
        ) : (
          <ChevronRightIcon className="w-3 h-3" />
        )}
        <span className="font-semibold">TOOL</span>
        <span className="text-muted-foreground">{formatToolName(event.tool_name)}</span>
        {event.is_async && <span className="text-purple-400">[async]</span>}
      </button>

      {/* Progress bar - shows inline when progress is available */}
      {progress && (
        <div className="ml-5 mt-1 flex items-center gap-2">
          <div className="flex-1 max-w-[200px] h-1.5 bg-muted border border-border rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progress.percent_complete}%` }}
            />
          </div>
          <span className="text-[10px] text-blue-600 dark:text-blue-400 min-w-[60px]">
            {progress.state} {Math.round(progress.percent_complete)}%
          </span>
        </div>
      )}

      {expanded && (
        <div className="mt-2 ml-5">
          <JsonCodeBlock
            data={event.tool_input}
            title="Input"
            defaultExpanded={true}
            maxHeight="250px"
          />
        </div>
      )}
    </div>
  );
}

/**
 * Parse result that may be wrapped in MCP content format or stringified
 */
function unwrapMcpResult(result: unknown): unknown {
  if (!result) return null;

  // If it's a string, try to parse as JSON
  if (typeof result === 'string') {
    try {
      return JSON.parse(result);
    } catch {
      return result; // Not JSON, return as-is
    }
  }

  // If it's an object with MCP content wrapper
  if (typeof result === 'object') {
    const r = result as Record<string, unknown>;

    // MCP format: { content: [{ type: 'text', text: '...' }] }
    if (Array.isArray(r.content) && r.content.length > 0) {
      const firstContent = r.content[0] as Record<string, unknown>;
      if (firstContent?.type === 'text' && typeof firstContent.text === 'string') {
        try {
          return JSON.parse(firstContent.text);
        } catch {
          return firstContent.text;
        }
      }
    }
  }

  return result;
}

/**
 * Extract resource list from tool result
 * Handles discover_data_sources and similar tools
 * Supports multiple result formats: MCP-wrapped, direct object, or stringified
 */
function extractResourceList(result: unknown): { sources: unknown[]; metadata?: Record<string, unknown> } | null {
  // First unwrap any MCP/string wrapping
  const unwrapped = unwrapMcpResult(result);
  if (!unwrapped || typeof unwrapped !== 'object') return null;

  const r = unwrapped as Record<string, unknown>;

  // Direct sources array
  if (Array.isArray(r.sources)) {
    return { sources: r.sources, metadata: r.metadata as Record<string, unknown> };
  }

  // Nested in data object (discover_data_sources format)
  if (r.data && typeof r.data === 'object') {
    const data = r.data as Record<string, unknown>;
    if (Array.isArray(data.sources)) {
      return {
        sources: data.sources,
        metadata: {
          ...data.metadata as Record<string, unknown>,
          grouped_by_type: data.grouped_by_type,
        }
      };
    }
  }

  return null;
}

/**
 * Resource form data structure from get_data_resource_schema tool
 */
interface ResourceFormData {
  type: string;           // Full resource type path
  display_name: string;   // Human-readable name
  fields: unknown[];      // Field definitions
  action?: "create" | "edit" | "view";
  values?: Record<string, unknown>;
  resource_id?: string;
  message?: string;
}

/**
 * Extract resource form schema from tool result
 * Handles get_data_resource_schema and similar tools
 */
function extractResourceForm(result: unknown): ResourceFormData | null {
  // First unwrap any MCP/string wrapping
  const unwrapped = unwrapMcpResult(result);
  if (!unwrapped || typeof unwrapped !== 'object') return null;

  const r = unwrapped as Record<string, unknown>;

  // Check for schema with fields array (get_data_resource_schema response)
  if (r.type && typeof r.type === 'string' && Array.isArray(r.fields)) {
    return {
      type: r.type as string,
      display_name: (r.display_name as string) || r.type as string,
      fields: r.fields,
      action: (r.action as "create" | "edit" | "view") || "create",
      values: r.values as Record<string, unknown>,
      resource_id: r.resource_id as string,
      message: r.message as string,
    };
  }

  // Check for nested data object
  if (r.data && typeof r.data === 'object') {
    const data = r.data as Record<string, unknown>;
    if (data.type && typeof data.type === 'string' && Array.isArray(data.fields)) {
      return {
        type: data.type as string,
        display_name: (data.display_name as string) || data.type as string,
        fields: data.fields,
        action: (data.action as "create" | "edit" | "view") || "create",
        values: data.values as Record<string, unknown>,
        resource_id: data.resource_id as string,
        message: data.message as string,
      };
    }
  }

  return null;
}

function ToolCallCompletedEntry({ event, toolInput, onFormSubmit }: { event: ToolCallCompletedEvent; toolInput: Record<string, unknown>; onFormSubmit?: (message: string) => void }) {
  const [expanded, setExpanded] = useState(false);

  // Check for custom renderer
  const CustomRenderer = toolResultRenderers[event.tool_name];
  if (CustomRenderer) {
    return (
      <CustomRenderer
        toolName={event.tool_name}
        toolInput={toolInput}
        result={event.result}
        isError={event.is_error}
        durationMs={event.duration_ms}
      />
    );
  }

  // Extract any file outputs from the result
  const fileOutputs = useMemo(
    () => extractFileOutputs(event.result),
    [event.result]
  );
  const hasFiles = fileOutputs.length > 0;

  // Check for resource list data (discover_data_sources, etc.)
  const resourceData = useMemo(() => {
    const unwrapped = unwrapMcpResult(event.result);
    const data = extractResourceList(event.result);
    // Debug: log the extraction attempt for discover_data_sources
    if (event.tool_name.includes('discover') || event.tool_name.includes('data_sources')) {
      const rawStr = JSON.stringify(event.result)?.slice(0, 500);
      const unwrappedStr = JSON.stringify(unwrapped)?.slice(0, 500);
      console.log('🔍 extractResourceList for', event.tool_name);
      console.log('  📥 rawResult type:', typeof event.result, '| preview:', rawStr);
      console.log('  📦 unwrapped type:', typeof unwrapped, '| preview:', unwrappedStr);
      console.log('  ✅ extracted:', data ? `${data.sources.length} sources` : 'null');
      if (!data && unwrapped && typeof unwrapped === 'object') {
        const keys = Object.keys(unwrapped as object);
        console.log('  🔑 unwrapped keys:', keys);
      }
    }
    return data;
  }, [event.result, event.tool_name]);

  // Check for resource form data (get_data_resource_schema, etc.)
  const formData = useMemo(() => {
    const data = extractResourceForm(event.result);
    // Debug: log the extraction attempt for schema tools
    if (event.tool_name.includes('schema') || event.tool_name.includes('resource')) {
      console.log('📝 extractResourceForm for', event.tool_name);
      console.log('  ✅ extracted:', data ? `${data.display_name} with ${data.fields.length} fields` : 'null');
    }
    return data;
  }, [event.result, event.tool_name]);

  // Render resource list as DataTable
  if (resourceData && resourceData.sources.length > 0) {
    const payload = {
      type: "resource_list" as const,
      resources: resourceData.sources.map((s: unknown) => {
        const src = s as Record<string, unknown>;
        return {
          source_id: String(src.source_id || ''),
          source_name: String(src.source_name || ''),
          source_type: String(src.source_type || ''),
          connection_type: String(src.connection_type || ''),
          scope: src.scope as string,
          available: src.available as boolean,
          created_at: src.created_at as string,
        };
      }),
      metadata: {
        total_count: resourceData.metadata?.total_count as number || resourceData.sources.length,
        grouped_by_type: resourceData.metadata?.grouped_by_type as Record<string, number>,
      },
    };

    return (
      <div className="border-l-2 border-cyan-500 pl-3 py-1">
        <div className="flex items-center gap-2 text-cyan-400 mb-2">
          <CheckCircledIcon className="w-3 h-3" />
          <span className="font-semibold">RESULT</span>
          <span className="text-muted-foreground">{event.tool_name}</span>
          <span className="text-muted-foreground">|</span>
          <span className="text-muted-foreground">{event.duration_ms}ms</span>
          <span className="text-muted-foreground">|</span>
          <span className="text-emerald-400">{payload.resources.length} resources</span>
        </div>
        <div className="ml-2">
          <ResourceListMessage payload={payload} />
        </div>
      </div>
    );
  }

  // Render resource form if form data detected
  if (formData && formData.fields.length > 0 && onFormSubmit) {
    const handleFormSubmit = (values: Record<string, unknown>) => {
      // Send form values as new message to agent
      const message = JSON.stringify({
        action: formData.action || "create",
        resource_type: formData.type,
        resource_id: formData.resource_id,
        values: values,
      });
      onFormSubmit(message);
    };

    const handleFormCancel = () => {
      onFormSubmit(JSON.stringify({ action: "cancel" }));
    };

    // Convert to ResourceFormPayload for ResourceFormMessage
    const formPayload = {
      type: "resource_form" as const,
      action: formData.action || "create",
      resource_type: formData.type,
      resource_id: formData.resource_id,
      schema: {
        type: formData.type,
        display_name: formData.display_name,
        fields: formData.fields,
      },
      values: formData.values,
      message: formData.message,
    };

    return (
      <div className="border-l-2 border-emerald-500 pl-3 py-1">
        <div className="flex items-center gap-2 text-emerald-400 mb-2">
          <CheckCircledIcon className="w-3 h-3" />
          <span className="font-semibold">FORM</span>
          <span className="text-muted-foreground">{event.tool_name}</span>
          <span className="text-muted-foreground">|</span>
          <span className="text-muted-foreground">{event.duration_ms}ms</span>
          <span className="text-muted-foreground">|</span>
          <span className="text-emerald-400">{formData.display_name}</span>
        </div>
        <div className="ml-2 max-w-xl">
          <ResourceFormMessage payload={formPayload} onSubmit={(response) => onFormSubmit(response)} />
        </div>
      </div>
    );
  }

  // Default fallback UI
  return (
    <div className={cn(
      "border-l-2 pl-3 py-1",
      event.is_error ? "border-red-500" : "border-cyan-500"
    )}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "flex items-center gap-2 hover:opacity-80 transition-opacity w-full text-left",
          event.is_error ? "text-red-400" : "text-cyan-400"
        )}
      >
        {expanded ? (
          <ChevronDownIcon className="w-3 h-3" />
        ) : (
          <ChevronRightIcon className="w-3 h-3" />
        )}
        {event.is_error ? (
          <CrossCircledIcon className="w-3 h-3" />
        ) : (
          <CheckCircledIcon className="w-3 h-3" />
        )}
        <span className="font-semibold">RESULT</span>
        <span className="text-muted-foreground">{event.tool_name}</span>
        <span className="text-muted-foreground">|</span>
        <span className="text-muted-foreground">{event.duration_ms}ms</span>
        {hasFiles && (
          <>
            <span className="text-muted-foreground">|</span>
            <span className="text-blue-400">{fileOutputs.length} file{fileOutputs.length > 1 ? 's' : ''}</span>
          </>
        )}
      </button>

      {/* Always show file links if present */}
      {hasFiles && (
        <div className="mt-2 ml-5 flex flex-wrap gap-2">
          {fileOutputs.map((file, idx) => (
            <FileLink key={file.file_id || idx} file={file} />
          ))}
        </div>
      )}

      {expanded && (
        <div className="mt-2 ml-5">
          {typeof event.result === "string" ? (
            <div className="bg-terminal-card text-terminal-foreground rounded p-2">
              <pre className={cn(
                "whitespace-pre-wrap text-[10px]",
                event.is_error && "text-red-300"
              )}>
                {event.result}
              </pre>
            </div>
          ) : (
            <JsonCodeBlock
              data={event.result}
              title={event.is_error ? "Error" : "Output"}
              defaultExpanded={true}
              maxHeight="400px"
              className={event.is_error ? "border-red-500/50" : undefined}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ExecutionCompletedEntryRenderer({ event }: { event: ExecutionCompletedEvent }) {
  return (
    <div className="border-l-2 border-green-500 pl-3 py-1">
      <div className="flex items-center gap-2 text-green-400">
        <CheckCircledIcon className="w-3 h-3" />
        <span className="font-semibold">DONE</span>
        <span className="text-muted-foreground">|</span>
        <span className="text-muted-foreground">{(event.duration_ms / 1000).toFixed(1)}s total</span>
        <span className="text-muted-foreground">|</span>
        <span className="text-amber-400">{event.total_input_tokens}+{event.total_output_tokens} tok</span>
        <span className="text-muted-foreground">|</span>
        <span className="text-green-400">${event.cost_usd.toFixed(4)}</span>
      </div>
    </div>
  );
}

function ToolsFinderStartedEntry({ event }: { event: ToolsFinderStartedEvent }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-l-2 border-orange-500 pl-3 py-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors w-full text-left"
      >
        {expanded ? (
          <ChevronDownIcon className="w-3 h-3" />
        ) : (
          <ChevronRightIcon className="w-3 h-3" />
        )}
        <span className="font-semibold">TOOLS FINDER</span>
        <span className="text-muted-foreground">searching alternatives for {event.count} unavailable tools</span>
      </button>
      {expanded && event.unavailable_tools.length > 0 && (
        <div className="mt-2 ml-5 bg-terminal-card text-terminal-foreground rounded p-2">
          <div className="text-terminal-muted mb-1">Unavailable tools:</div>
          <div className="flex flex-wrap gap-1">
            {event.unavailable_tools.map((tool, idx) => (
              <span key={idx} className="text-orange-300 bg-orange-950 px-1.5 py-0.5 rounded text-[10px]">
                {tool}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ToolsFinderCompletedEntry({ event }: { event: ToolsFinderCompletedEvent }) {
  const [expanded, setExpanded] = useState(false);
  const hasAlternatives = event.alternatives_found > 0;

  return (
    <div className="border-l-2 border-orange-500 pl-3 py-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors w-full text-left"
      >
        {expanded ? (
          <ChevronDownIcon className="w-3 h-3" />
        ) : (
          <ChevronRightIcon className="w-3 h-3" />
        )}
        <CheckCircledIcon className="w-3 h-3 text-green-400" />
        <span className="font-semibold">TOOLS FINDER</span>
        <span className="text-muted-foreground">
          {hasAlternatives
            ? `found ${event.alternatives_found} alternatives for ${event.total_tools} tools`
            : `no alternatives found for ${event.total_tools} tools`
          }
        </span>
      </button>
      {expanded && Object.keys(event.results).length > 0 && (
        <div className="mt-2 ml-5">
          <JsonCodeBlock
            data={event.results}
            title="Results"
            defaultExpanded={true}
            maxHeight="300px"
          />
        </div>
      )}
    </div>
  );
}

function AsyncToolCheckpointEntry({ event }: { event: AsyncToolCheckpointEvent }) {
  const [expanded, setExpanded] = useState(false);
  const hasData = event.checkpoint_data && Object.keys(event.checkpoint_data).length > 0;

  return (
    <div className="border-l-2 border-cyan-500 pl-3 py-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors w-full text-left"
      >
        {hasData ? (
          expanded ? (
            <ChevronDownIcon className="w-3 h-3" />
          ) : (
            <ChevronRightIcon className="w-3 h-3" />
          )
        ) : (
          <span className="w-3 h-3" />
        )}
        <span className="font-semibold">CHECKPOINT</span>
        <span className="text-muted-foreground">
          {event.tool_name} ({event.tool_use_id.slice(0, 8)}...)
        </span>
      </button>
      {expanded && hasData && (
        <div className="mt-2 ml-5">
          <JsonCodeBlock
            data={event.checkpoint_data}
            title="Checkpoint Data"
            defaultExpanded={true}
            maxHeight="300px"
          />
        </div>
      )}
    </div>
  );
}

// ProCode subagent LLM lifecycle event renderers

function LLMStartEntry({ event }: { event: LLMStartEvent }) {
  return (
    <div className="border-l-2 border-yellow-500/50 pl-3 py-0.5">
      <div className="flex items-center gap-2 text-yellow-500 text-xs">
        <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
        <span className="font-semibold">LLM</span>
        {event.agent_name && (
          <span className="text-muted-foreground">{event.agent_name}</span>
        )}
        <span className="text-yellow-500/70">thinking...</span>
      </div>
    </div>
  );
}

function LLMEndEntry({ event }: { event: LLMEndEvent }) {
  return (
    <div className="border-l-2 border-yellow-500 pl-3 py-0.5">
      <div className="flex items-center gap-2 text-yellow-500 text-xs">
        <CheckCircledIcon className="w-3 h-3" />
        <span className="font-semibold">LLM</span>
        {event.agent_name && (
          <span className="text-muted-foreground">{event.agent_name}</span>
        )}
        <span className="text-muted-foreground">|</span>
        <span className="text-amber-400">{event.input_tokens}+{event.output_tokens} tok</span>
        {event.cost_usd > 0 && (
          <>
            <span className="text-muted-foreground">|</span>
            <span className="text-green-400">${event.cost_usd.toFixed(4)}</span>
          </>
        )}
      </div>
    </div>
  );
}

function AgentStartEntry({ event }: { event: AgentStartEvent }) {
  return (
    <div className="border-l-2 border-indigo-500/50 pl-3 py-0.5">
      <div className="flex items-center gap-2 text-indigo-400 text-xs">
        <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
        <span className="font-semibold">AGENT</span>
        {event.agent_name && (
          <span className="text-muted-foreground">{event.agent_name}</span>
        )}
      </div>
    </div>
  );
}
